import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, Lock, User as UserIcon, Loader2, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/AuthContext";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import DemoDialog from "@/components/DemoDialog";

const GOOGLE_SIGNIN_BASE = "https://auth.emergentagent.com/";

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, login, register } = useAuth();
  const product = searchParams.get("product");
  // SpaceCost auth links pre-target the SpaceCost tab on the dashboard.
  const redirect = searchParams.get("redirect")
    || (product === "spacecost" ? "/projects?product=spacecost" : "/projects");
  const initialMode = searchParams.get("mode") === "register" || searchParams.get("mode") === "signup"
    ? "signup" : "login";
  const [mode, setMode] = useState(initialMode); // 'login' | 'signup'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);

  const isSpaceCost = product === "spacecost";
  // Brand strings flip with the `?product=` param so users who started in
  // SpaceCost don't suddenly see "FlipCost" wordmarks/copy. The logo asset
  // is shared (no dedicated SpaceCost mark exists yet) but the alt text
  // and homeLabel rotate. The wordmark uses the BY MYPLANCOST caption from
  // the landing pages so the brand reads consistently across the site.
  const brand = isSpaceCost
    ? {
        name: "SpaceCost",
        homeLabel: "MyPlanCost",
      }
    : {
        name: "FlipCost",
        homeLabel: "MyPlanCost",
      };

  // Switch product without losing the user's current mode / redirect params.
  // When the active redirect target is the default `/projects` we also flip
  // the `?product=` query of that target so the right tab is pre-selected.
  const switchProduct = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "spacecost") params.set("product", "spacecost");
    else params.delete("product");
    setSearchParams(params, { replace: true });
  };

  // If already authenticated, bounce straight to the redirect target.
  useEffect(() => {
    if (user && user !== false && user !== null) {
      navigate(redirect, { replace: true });
    }
  }, [user, redirect, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      if (mode === "signup") {
        await register(email.trim(), password, name.trim());
        toast.success("Welcome aboard!");
      } else {
        await login(email.trim(), password);
        toast.success("Signed in");
      }
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `${GOOGLE_SIGNIN_BASE}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div
      className="min-h-screen fc-app-bg grid lg:grid-cols-[1.1fr_1fr]"
      data-testid="auth-page"
      data-theme={isSpaceCost ? "spacecost" : undefined}
    >
      {/* Left — brand / value prop */}
      <aside className="hidden lg:flex flex-col justify-between p-12 border-r border-black/[0.06] bg-[radial-gradient(circle_at_top_left,rgba(106,130,96,0.12),transparent_60%)]">
        <div className="space-y-5">
          <Link
            to="/"
            data-testid="auth-back-home-aside"
            className="inline-flex items-center gap-1.5 text-[12px] text-[#78716C] hover:text-[#1C1C1C] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to {brand.homeLabel}
          </Link>
          <Link
            to="/"
            className="flex items-baseline gap-2.5 sm:gap-3 group"
            data-testid="auth-logo-link"
            aria-label={`${brand.name} by MyPlanCost — home`}
          >
            <span className="fc-heading text-2xl sm:text-[28px] font-extrabold tracking-tight text-[#1C1C1C] leading-none">
              {brand.name}
            </span>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
              by MyPlanCost
            </span>
          </Link>
        </div>

        <div className="max-w-lg">
          <h1 className="fc-heading text-4xl lg:text-5xl tracking-tight text-[#1C1C1C] leading-[1.05]">
            {isSpaceCost
              ? "Cost the room before you swing the first hammer."
              : "Know your renovation numbers, down to the last line item."}
          </h1>
          <p className="text-[#57534E] text-[16px] mt-5 leading-relaxed">
            Sign in to plan your renovations, compare options, track actual spend against budget,
            and keep every project organised in one place.
          </p>
          <ul className="mt-8 space-y-2.5 text-sm text-[#57534E]">
            <li className="flex items-start gap-2.5">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage-700" />
              One MyPlanCost account works across FlipCost and SpaceCost.
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage-700" />
              Your projects are private to you — always.
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sage-700" />
              Sign in with Google or email — your choice.
            </li>
          </ul>
        </div>

        <div className="text-[11px] text-[#A8A29E]">
          <Link
            to="/disclaimer"
            data-testid="auth-aside-disclaimer"
            className="underline hover:text-[#1C1C1C]"
          >
            Disclaimer &amp; Terms
          </Link>{" "}
          · © {new Date().getFullYear()} MyPlanCost
        </div>
      </aside>

      {/* Right — form */}
      <section className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[420px]">
          {/* Mobile back-home + brand */}
          <div className="lg:hidden mb-6">
            <Link
              to="/"
              data-testid="auth-back-home-mobile"
              className="inline-flex items-center gap-1.5 text-[12px] text-[#78716C] hover:text-[#1C1C1C] transition-colors mb-4"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to {brand.homeLabel}
            </Link>
            <Link
              to="/"
              className="flex items-baseline gap-2.5"
              data-testid="auth-logo-mobile"
              aria-label={`${brand.name} by MyPlanCost — home`}
            >
              <span className="fc-heading text-2xl font-extrabold tracking-tight text-[#1C1C1C] leading-none">
                {brand.name}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#A8A29E] font-medium leading-none whitespace-nowrap">
                by MyPlanCost
              </span>
            </Link>
          </div>

          {/* Product switcher — quietly lets a user flip the auth context
              between FlipCost and SpaceCost without bouncing back to the
              umbrella home. Hidden when the user arrived with an explicit
              `?redirect=` (e.g. invite flow) so we don't change the
              destination on them. */}
          {!searchParams.get("redirect") && (
            <div
              role="tablist"
              aria-label="Choose product"
              data-testid="auth-product-switcher"
              className="inline-flex p-1 mb-5 rounded-full bg-black/[0.04] border border-black/[0.06] text-[12px] font-medium"
            >
              {[
                { id: "flipcost", label: "FlipCost" },
                { id: "spacecost", label: "SpaceCost" },
              ].map((p) => {
                const active = (p.id === "spacecost") === isSpaceCost;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`auth-product-switch-${p.id}`}
                    onClick={() => !active && switchProduct(p.id)}
                    className={`px-3.5 h-7 rounded-full transition-colors ${
                      active
                        ? "bg-white text-[#1C1C1C] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                        : "text-[#78716C] hover:text-[#1C1C1C]"
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}

          <h2
            className="fc-heading text-2xl text-[#1C1C1C]"
            data-testid="auth-heading"
          >
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-sm text-[#78716C] mt-1.5">
            {mode === "login"
              ? "Sign in to pick up where you left off."
              : "A few seconds to set up. No credit card needed."}
          </p>

          {/* Demo CTA — visible above the auth form so prospects don't have to sign up to look around */}
          <button
            type="button"
            data-testid="auth-demo-btn"
            onClick={() => setDemoOpen(true)}
            className="mt-5 w-full h-11 rounded-lg bg-sage-100 hover:bg-sage-200 border border-sage-200 text-sage-700 hover:text-sage-800 text-sm font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <Play className="w-3.5 h-3.5" /> Try the Live Demo first — no signup
          </button>

          <button
            type="button"
            onClick={googleSignIn}
            data-testid="auth-google-btn"
            className="mt-7 w-full h-11 rounded-lg border border-black/[0.12] hover:border-black/[0.28] bg-white text-[#1C1C1C] text-sm font-medium flex items-center justify-center gap-3 transition-colors"
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-black/[0.08]" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A8A29E]">or</div>
            <div className="flex-1 h-px bg-black/[0.08]" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <Field label="Name" icon={UserIcon}>
                <Input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="fc-input h-11 pl-10"
                />
              </Field>
            )}
            <Field label="Email" icon={Mail}>
              <Input
                data-testid="auth-email-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="fc-input h-11 pl-10"
              />
            </Field>
            <Field label="Password" icon={Lock}>
              <Input
                data-testid="auth-password-input"
                type="password"
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === "signup" ? "At least 6 characters" : "••••••••"}
                required
                minLength={6}
                className="fc-input h-11 pl-10"
              />
            </Field>
            {mode === "login" && (
              <div className="flex justify-end -mt-1.5">
                <button
                  type="button"
                  data-testid="auth-forgot-password-btn"
                  onClick={() => setForgotOpen(true)}
                  className="text-[11px] text-[#78716C] hover:text-[#1C1C1C] underline underline-offset-2 transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            )}

            {error && (
              <div
                data-testid="auth-error"
                className="text-xs text-[#DC2626] bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={submitting}
              className="w-full h-11 rounded-lg bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] text-sm font-medium flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </Button>

            {mode === "signup" && (
              <p
                className="text-[11px] text-[#78716C] leading-relaxed pt-1"
                data-testid="auth-disclaimer-ack"
              >
                By creating an account you acknowledge that {brand.name} outputs
                are <strong>estimates only</strong> — not quotes, valuations, or
                financial advice — and you agree to our{" "}
                <Link
                  to="/disclaimer"
                  className="underline hover:text-[#1C1C1C]"
                  data-testid="auth-disclaimer-link"
                >
                  Disclaimer &amp; Terms of Use
                </Link>
                .
              </p>
            )}
          </form>

          <div className="text-center mt-6 text-sm text-[#78716C]">
            {mode === "login" ? (
              <>
                New to {brand.name}?{" "}
                <button
                  type="button"
                  data-testid="auth-switch-to-signup"
                  onClick={() => { setMode("signup"); setError(""); }}
                  className="text-[#1C1C1C] font-medium underline underline-offset-2 hover:text-sage-700"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  data-testid="auth-switch-to-login"
                  onClick={() => { setMode("login"); setError(""); }}
                  className="text-[#1C1C1C] font-medium underline underline-offset-2 hover:text-sage-700"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </section>
      <ForgotPasswordDialog
        open={forgotOpen}
        onOpenChange={setForgotOpen}
        defaultEmail={email}
      />
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <div className="fc-label mb-1.5">{label}</div>
      <div className="relative">
        {Icon && (
          <Icon className="w-4 h-4 text-[#A8A29E] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        )}
        {children}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.2a10.34 10.34 0 0 0-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9a8.78 8.78 0 0 0 2.7-6.62z" fill="#4285F4" />
      <path d="M9 18a8.58 8.58 0 0 0 5.96-2.18l-2.9-2.26a5.4 5.4 0 0 1-8.06-2.84H1.02v2.32A9 9 0 0 0 9 18z" fill="#34A853" />
      <path d="M3.96 10.72a5.4 5.4 0 0 1 0-3.44V4.96H1.02a9 9 0 0 0 0 8.08l2.94-2.32z" fill="#FBBC05" />
      <path d="M9 3.58a4.86 4.86 0 0 1 3.44 1.34l2.58-2.58A8.64 8.64 0 0 0 9 0 9 9 0 0 0 1.02 4.96l2.94 2.32A5.4 5.4 0 0 1 9 3.58z" fill="#EA4335" />
    </svg>
  );
}
